import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  BookOpen,
  Video,
  Clock,
  User,
  Plus,
  Play,
  Home,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getCourses, getMyEnrollments, enrollCourse } from '../../services/api';

// Import specialized layouts
import BasicEbooksLayout from './BasicEbooksLayout.jsx';
import IntermediateEbooksLayout from './IntermediateEbooksLayout.jsx';
import AdvancedEbooksLayout from './AdvancedEbooksLayout.jsx';
import TradingViewCoursesLayout from './TradingViewCoursesLayout.jsx';
import IndicatorCoursesLayout from './IndicatorCoursesLayout.jsx';
import GeneralCoursesLayout from './GeneralCoursesLayout.jsx';

const Courses = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');

  // Check URL parameters for specialized layouts
  const courseType = searchParams.get('courseType');
  const ebookSubcategory = searchParams.get('ebookSubcategory');
  const videoCategory = searchParams.get('videoCategory');

  const isBasicEbooks = courseType === 'ebook' && ebookSubcategory === 'basic';
  const isIntermediateEbooks = courseType === 'ebook' && ebookSubcategory === 'intermediate';
  const isAdvancedEbooks = courseType === 'ebook' && ebookSubcategory === 'advanced';
  const isTradingViewCourses = courseType === 'video' && videoCategory === 'tradingview';
  const isIndicatorCourses = courseType === 'video' && videoCategory === 'indicator';
  const isGeneralCourses = courseType === 'video' && videoCategory === 'general';

  // Determine if we should show specialized layout
  const showSpecializedLayout = isBasicEbooks || isIntermediateEbooks || isAdvancedEbooks ||
    isTradingViewCourses || isIndicatorCourses || isGeneralCourses;

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  useEffect(() => {
    if (user && !showSpecializedLayout) {
      fetchCourses();
      fetchEnrollments();
    }
  }, [user, searchQuery, selectedType, selectedLevel, showSpecializedLayout]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const params = { limit: 50 };

      if (selectedType !== 'all') {
        params.courseType = selectedType;
      }
      if (selectedLevel !== 'all') {
        params.level = selectedLevel;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await getCourses(params);
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrollments = async () => {
    try {
      const response = await getMyEnrollments();
      setEnrollments(response.data.enrollments || []);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
    }
  };

  const handleEnroll = async (courseId) => {
    try {
      await enrollCourse(courseId);
      fetchEnrollments();
    } catch (error) {
      console.error('Error enrolling in course:', error);
      alert('Failed to enroll in course');
    }
  };

  const isEnrolled = (courseId) => {
    return enrollments.some((enrollment) =>
      enrollment.course?._id === courseId || enrollment.course === courseId
    );
  };

  const getEnrollment = (courseId) => {
    return enrollments.find((enrollment) =>
      enrollment.course?._id === courseId || enrollment.course === courseId
    );
  };

  const CourseCard = ({ course }) => {
    const enrolled = isEnrolled(course._id);
    const enrollment = getEnrollment(course._id);

    const getCourseTypeColor = () => {
      if (course.courseType === 'ebook') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      if (course.courseType === 'video') return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    };

    const getLevelColor = () => {
      const level = course.level?.toLowerCase() || course.ebookSubcategory?.toLowerCase();
      if (level === 'basic' || level === 'beginner') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      if (level === 'intermediate') return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
      if (level === 'advanced') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    };

    return (
      <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden transition-all duration-200 hover:shadow-lg group">
        <div className="aspect-[16/9] overflow-hidden relative">
          <img
            src={course.thumbnail || 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=800&h=600&q=80'}
            alt={course.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
              {course.courseType === 'video' ? (
                <Play size={24} className="text-[#30C4B5] ml-1" />
              ) : (
                <BookOpen size={24} className="text-[#30C4B5]" />
              )}
            </div>
          </div>
        </div>

        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getCourseTypeColor()}`}>
              {course.courseType === 'ebook' ? (
                <><BookOpen size={12} className="mr-1" /> Ebook</>
              ) : course.courseType === 'video' ? (
                <><Video size={12} className="mr-1" /> Video Course</>
              ) : (
                <><BookOpen size={12} className="mr-1" /> Course</>
              )}
            </span>
            <span className={`px-2 py-1 rounded text-xs font-medium ${getLevelColor()}`}>
              {course.level || course.ebookSubcategory || 'General'}
            </span>
          </div>

          <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2 line-clamp-2">
            {course.title}
          </h3>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
            {course.shortDescription}
          </p>

          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-4 space-x-4">
            <div className="flex items-center">
              <User size={12} className="mr-1" />
              {course.instructor?.firstName || 'Instructor'}
            </div>
            <div className="flex items-center">
              <BookOpen size={12} className="mr-1" />
              {course.totalLessons || 0} lessons
            </div>
            {course.totalDuration && (
              <div className="flex items-center">
                <Clock size={12} className="mr-1" />
                {course.totalDuration}
              </div>
            )}
          </div>

          {enrolled ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500 dark:text-gray-400">Progress</span>
                <span className="text-xs font-medium text-gray-900 dark:text-white">
                  {enrollment?.progressPercentage || 0}%
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#30C4B5] rounded-full transition-all duration-300"
                  style={{ width: `${enrollment?.progressPercentage || 0}%` }}
                />
              </div>
              <button
                onClick={() => navigate(`/courses/${course._id}`)}
                className="w-full py-2 bg-[#30C4B5] hover:bg-[#29AF9F] text-white font-medium text-sm rounded-lg transition-colors"
              >
                Continue Learning
              </button>
            </div>
          ) : (
            <button
              onClick={() => handleEnroll(course._id)}
              className="w-full py-2 bg-gray-900 hover:bg-gray-800 text-white font-medium text-sm rounded-lg transition-colors flex items-center justify-center"
            >
              <Plus size={16} className="mr-2" />
              Enroll Now
            </button>
          )}
        </div>
      </div>
    );
  };

  // NOW WE CAN DO CONDITIONAL RETURNS AFTER ALL HOOKS
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return null;
  }

  // Render specialized layouts if needed
  if (isBasicEbooks) return <BasicEbooksLayout />;
  if (isIntermediateEbooks) return <IntermediateEbooksLayout />;
  if (isAdvancedEbooks) return <AdvancedEbooksLayout />;
  if (isTradingViewCourses) return <TradingViewCoursesLayout />;
  if (isIndicatorCourses) return <IndicatorCoursesLayout />;
  if (isGeneralCourses) return <GeneralCoursesLayout />;

  return (
    <div className="min-h-screen bg-gray-50 dark:!bg-black">
      <main className="max-w-[1400px] mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm mb-4">
            <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-[#30C4B5] transition-colors flex items-center">
              <Home size={14} className="mr-1" />
              Dashboard
            </Link>
            <ChevronRight size={14} className="text-gray-400" />
            <span className="text-gray-900 dark:text-white font-medium">All Courses</span>
          </nav>

          {/* Title */}
          <div className="mb-4">
            <h1 className="font-bold text-3xl text-gray-900 dark:text-white mb-2">
              All Courses
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Discover and enroll in trading courses to enhance your skills.
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#30C4B5] focus:border-[#30C4B5] transition-all"
              />
            </div>

            {/* Content Type Filter */}
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#30C4B5] focus:border-[#30C4B5] transition-all"
            >
              <option value="all">All Types</option>
              <option value="ebook">Ebooks</option>
              <option value="video">Video Courses</option>
              <option value="course">Courses</option>
            </select>

            {/* Level Filter */}
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#30C4B5] focus:border-[#30C4B5] transition-all"
            >
              <option value="all">All Levels</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>

            {/* Results Count */}
            <div className="flex items-center justify-end">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {courses.length} courses found
              </span>
            </div>
          </div>
        </div>

        {/* Courses Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6 animate-pulse">
                <div className="aspect-[16/9] bg-gray-200 dark:bg-gray-700 rounded-lg mb-4"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : courses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <CourseCard key={course._id} course={course} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800">
            <BookOpen size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2">
              No courses found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try adjusting your search criteria or filters.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Courses;